// Formal assigned-quiz attempt page (the "quizzes" collection / exam-mode
// system Phase 3's backend extended with negative marking + numerical/
// assertion-reason grading — a separate system from QAView.jsx's ephemeral
// per-lesson AI practice quizzes). Restyled onto the MyDeskey shell
// (var(--st-*) tokens) so it renders inside StudentApp instead of the old
// standalone DashboardShell chrome, which would have doubled up header/nav.
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Award, Check, X as XIcon, Clock, AlertTriangle, MessageCircle, Play, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import api from './student/studentApi';
import { showEarnedToasts } from './student/BadgeUnlockToast';
import { getExamPrepColors } from './student/examPrepTheme';
import { useStudent } from './student/StudentApp';
import { MathText } from '../components/RenderContent';
import { notifyError } from '../lib/errors';

const NUMERICAL_TOLERANCE = 1e-6;
const NEGATIVE_MARKING_TYPES = new Set(['mcq', 'assertion_reason']);
const GRADABLE_TYPES = new Set(['mcq', 'numerical', 'assertion_reason']);

function formatClock(totalSeconds) {
  const s = Math.max(0, totalSeconds || 0);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${String(m).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
}

// Mirrors backend exam_quiz_scoring._answers_match — informational only,
// the server-computed score/percentage is always the source of truth.
function answersMatch(type, given, correct) {
  if (given == null || correct == null) return false;
  const g = String(given).trim();
  const c = String(correct).trim();
  if (!g || !c) return false;
  if (type === 'numerical') {
    const gn = parseFloat(g);
    const cn = parseFloat(c);
    if (!Number.isNaN(gn) && !Number.isNaN(cn)) return Math.abs(gn - cn) < NUMERICAL_TOLERANCE;
  }
  return g.toUpperCase() === c.toUpperCase();
}

// sessionStorage can throw (private mode, blocked storage) — the attempt
// still works, it just won't survive a refresh.
function readDraft(key) {
  try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; }
}
function writeDraft(key, draft) {
  try { sessionStorage.setItem(key, JSON.stringify(draft)); } catch { /* not persisted */ }
}
function clearDraft(key) {
  try { sessionStorage.removeItem(key); } catch { /* nothing to clear */ }
}

export const StudentQuizAttempt = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { theme, me } = useStudent();
  // An in-progress attempt survives a refresh: answers + timed-test start time.
  const draftKey = `quiz-attempt:${me?.id || ''}:${quizId}`;
  const examPrep = getExamPrepColors(theme?.id);
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeReport, setTimeReport] = useState(null);
  // A previously-graded attempt for this quiz, loaded up front -- clicking
  // an already-attempted test from Practice should land on its result,
  // not silently drop the student straight back into a fresh attempt.
  // Only "Retake" clears it, gating back into the normal start/question flow.
  const [existingAttempt, setExistingAttempt] = useState(null);
  const [existingTimeReport, setExistingTimeReport] = useState(null);
  const [retaking, setRetaking] = useState(false);
  // Data-capture only, no visible timer UI. Tracks the timestamp of the
  // last interaction (quiz start, or the previous answer change) and the
  // accumulated time spent per question, so handleSubmit can report
  // time_spent_seconds/skipped for the time-report card below.
  const lastInteractionTimeRef = useRef(Date.now());
  const timeSpentByQuestionRef = useRef({});

  // Timed tests (quiz.time_limit set) gate behind a "Start Test" popup so
  // the countdown only begins when the student is actually ready --
  // started stays true for the rest of this component's life once set,
  // there's no pausing.
  const [started, setStarted] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const handleSubmitRef = useRef(() => {});
  const autoSubmittedRef = useRef(false);
  const startedAtRef = useRef(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    loadQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId]);

  const loadQuiz = async () => {
    try {
      setLoading(true);
      const [q, attempts] = await Promise.all([
        api.get(`/quizzes/${quizId}`),
        api.get(`/quiz-attempts?quiz_id=${quizId}`).catch((e) => { notifyError(e, "Couldn't load your earlier result."); return []; }),
      ]);
      setQuiz(q);
      lastInteractionTimeRef.current = Date.now();
      timeSpentByQuestionRef.current = {};

      const latest = (attempts || [])[0]; // list_quiz_attempts already sorts submitted_at desc
      const draft = readDraft(draftKey);
      if (draft) {
        setAnswers(draft.answers || {});
        if (latest) setRetaking(true);
        if (q.time_limit && draft.startedAt) {
          // Time keeps running while the page is closed — no extra time from a refresh.
          startedAtRef.current = draft.startedAt;
          setRemainingSeconds(Math.max(0, q.time_limit * 60 - Math.floor((Date.now() - draft.startedAt) / 1000)));
          setStarted(true);
        }
      }
      if (latest) {
        setExistingAttempt(latest);
        if (q.exam_mode) {
          api.get(`/student/exam-prep/quiz-attempts/${latest.id}/time-report`)
            .then(setExistingTimeReport)
            .catch(() => {}); // informational only
        }
      }
    } catch (error) {
      notifyError(error, 'Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const startTest = () => {
    startedAtRef.current = Date.now();
    writeDraft(draftKey, { answers, startedAt: startedAtRef.current });
    setRemainingSeconds((quiz.time_limit || 0) * 60);
    lastInteractionTimeRef.current = Date.now();
    setStarted(true);
  };

  const handleRetake = () => {
    setRetaking(true);
    setResult(null);
    setTimeReport(null);
    setAnswers({});
    setStarted(false);
    setRemainingSeconds(null);
    startedAtRef.current = null;
    autoSubmittedRef.current = false;
    timeSpentByQuestionRef.current = {};
    lastInteractionTimeRef.current = Date.now();
  };

  // Countdown, active only once started. Runs on the latest handleSubmit
  // closure via handleSubmitRef so this effect doesn't need to restart
  // (and drop ticks) every time answers/submitting change.
  useEffect(() => {
    if (!started || remainingSeconds === null) return;
    if (remainingSeconds <= 0) {
      if (!autoSubmittedRef.current) {
        autoSubmittedRef.current = true;
        toast.info("Time's up — submitting your test.");
        handleSubmitRef.current({ auto: true });
      }
      return;
    }
    const t = setTimeout(() => setRemainingSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [started, remainingSeconds]);

  const gradableQuestions = (quiz?.questions || []).filter(q => GRADABLE_TYPES.has(q.type));
  // SAT's exam_mode carries no negative marking (see backend
  // DEFAULT_NEGATIVE_MARKS_BY_EXAM) -- check the actual per-question
  // negative_marks rather than assuming every exam_mode penalises wrong
  // answers.
  const hasNegativeMarking = (quiz?.questions || []).some(q => (q.negative_marks || 0) > 0);

  const handleAnswerChange = (questionNumber, answer) => {
    const now = Date.now();
    const elapsedSeconds = Math.max(0, Math.round((now - lastInteractionTimeRef.current) / 1000));
    timeSpentByQuestionRef.current = {
      ...timeSpentByQuestionRef.current,
      [questionNumber]: (timeSpentByQuestionRef.current[questionNumber] || 0) + elapsedSeconds,
    };
    lastInteractionTimeRef.current = now;
    setAnswers(prev => {
      const next = { ...prev, [questionNumber]: answer };
      writeDraft(draftKey, { answers: next, startedAt: startedAtRef.current });
      return next;
    });
  };

  const handleSubmit = async ({ auto = false } = {}) => {
    const answeredCount = Object.keys(answers).length;
    // A timeout-triggered auto-submit must go through regardless of how
    // many questions are answered -- unanswered ones are reported
    // skipped:true below, same as an ordinary incomplete manual submit
    // would be if this check didn't block it.
    // Once time is up a manual retry must also go through, or a failed
    // auto-submit would leave the student with no way to submit.
    const timeUp = started && remainingSeconds !== null && remainingSeconds <= 0;
    if (!auto && !timeUp && answeredCount < gradableQuestions.length) {
      toast.error(`Please answer all questions. ${answeredCount}/${gradableQuestions.length} answered.`);
      return;
    }
    // The timer's auto-submit and a manual click can land together — post once.
    if (submittingRef.current) return;
    submittingRef.current = true;

    try {
      setSubmitting(true);
      // Every question gets an entry -- answered ones carry their captured
      // time_spent_seconds, and any question that never got an answer is
      // still reported (skipped: true) so compute_time_report has data.
      const answersArray = quiz.questions.map((q) => {
        const qNum = q.question_number;
        if (!Object.prototype.hasOwnProperty.call(answers, qNum)) {
          return { question_number: qNum, answer: null, skipped: true, time_spent_seconds: 0 };
        }
        return {
          question_number: qNum,
          answer: answers[qNum],
          skipped: false,
          time_spent_seconds: timeSpentByQuestionRef.current[qNum] || 0,
        };
      });

      const res = await api.post('/quiz-attempts', { quiz_id: quizId, answers: answersArray });
      clearDraft(draftKey);
      // The answer key is hidden until the first attempt; it comes back with the graded attempt.
      if (res.quiz_questions?.length) setQuiz(prev => ({ ...prev, questions: res.quiz_questions }));
      setResult(res);
      if (!auto) toast.success('Quiz submitted successfully!');
      showEarnedToasts(res.newly_earned_badges); // Exam Prep mocks feed Quiz badges

      if (quiz.exam_mode && res.id) {
        api.get(`/student/exam-prep/quiz-attempts/${res.id}/time-report`)
          .then(setTimeReport)
          .catch(() => {}); // informational only — never block the result screen
      }
    } catch (error) {
      // 400 = the server won't take this attempt (e.g. already submitted) — drop
      // the draft so a reload doesn't auto-submit it again.
      if (error?.status === 400) clearDraft(draftKey);
      notifyError(error, 'Failed to submit quiz');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };
  handleSubmitRef.current = handleSubmit;

  const discussWrongAnswer = (question, userAnswer) => {
    if (!quiz.published_lesson_id) return;
    navigate(`/student/mydesk/lesson/${quiz.published_lesson_id}`, {
      state: {
        wrongAnswerRef: {
          question_text: question.question,
          given_answer: userAnswer ?? '',
          correct_answer: question.correct_answer,
        },
      },
    });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-4" data-testid="quiz-attempt-loading">
        <div className="h-10 w-64 rounded-xl animate-pulse" style={{ background: 'var(--st-bg)' }} />
        <div className="h-96 rounded-2xl animate-pulse" style={{ background: 'var(--st-bg)' }} />
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-testid="quiz-not-found">
        <Award className="w-10 h-10 mx-auto opacity-30 mb-3" />
        <p style={{ color: 'var(--st-mute)' }}>Quiz not found</p>
        <button onClick={() => navigate('/student/quizzes')} className="mt-4 rounded-xl px-4 py-2 text-[13px] font-bold text-white" style={{ background: 'var(--st-primary)' }}>
          <ArrowLeft className="inline w-4 h-4 mr-1" /> Back to My Quizzes
        </button>
      </div>
    );
  }

  // A fresh submission (result) always wins; otherwise, an unretaken prior
  // attempt shows its result instead of dropping straight into a new one.
  const displayedResult = result || (!retaking ? existingAttempt : null);
  if (displayedResult) {
    return (
      <ResultView
        quiz={quiz} result={displayedResult} timeReport={result ? timeReport : existingTimeReport}
        answers={answers} onDiscuss={discussWrongAnswer} onRetake={handleRetake}
        onBack={() => navigate('/student/quizzes')}
      />
    );
  }

  if (quiz.time_limit && !started) {
    return (
      <StartTestPopup
        quiz={quiz} gradableCount={gradableQuestions.length} examPrep={examPrep}
        onStart={startTest} onBack={() => navigate('/student/quizzes')}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-5" data-testid="quiz-attempt-page">
      <button onClick={() => navigate('/student/quizzes')} data-testid="back-button" className="text-[12px] font-semibold inline-flex items-center gap-1 opacity-70 hover:opacity-100">
        <ArrowLeft className="w-4 h-4" /> Back to My Quizzes
      </button>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight" data-testid="quiz-title" style={{ color: 'var(--st-ink)', fontFamily: 'var(--st-font-heading)' }}>{quiz.title}</h1>
          <p className="text-[12.5px] mt-1" style={{ color: 'var(--st-mute)' }}>Answer all questions to submit</p>
        </div>
        <div className="flex items-center gap-2">
          {quiz.exam_mode && (
            <span className="text-[10px] font-extrabold uppercase tracking-wider rounded-full px-2.5 py-1" style={{ background: examPrep.gold, color: '#fff' }}>
              {quiz.exam_mode.replace('_', ' ')}{hasNegativeMarking && <> &middot; negative marking</>}
            </span>
          )}
          {quiz.time_limit && (
            <span className="text-[11px] font-bold rounded-full px-2.5 py-1 inline-flex items-center gap-1 border tabular-nums"
              data-testid="quiz-timer" style={{ borderColor: remainingSeconds <= 60 ? '#DC2626' : 'var(--st-border)', color: remainingSeconds <= 60 ? '#DC2626' : 'inherit' }}>
              <Clock className="w-3 h-3" /> {formatClock(remainingSeconds)}
            </span>
          )}
        </div>
      </div>

      <div className="rounded-2xl border divide-y" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
        {gradableQuestions.map((q, i) => (
          <div key={q.question_number} className="p-5 space-y-3" data-testid={`question-${i}`}>
            <div className="flex items-start gap-3">
              <span className="w-7 h-7 rounded-full text-xs font-extrabold text-white flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'var(--st-primary)' }}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <div className="font-semibold text-[14px]" style={{ color: 'var(--st-ink)' }}><MathText text={q.question} /></div>
                  {q.type === 'assertion_reason' && (
                    <span className="text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0" style={{ background: 'var(--st-bg)', color: 'var(--st-mute)' }}>Assertion-Reason</span>
                  )}
                  {quiz.exam_mode && NEGATIVE_MARKING_TYPES.has(q.type) && !!q.negative_marks && (
                    <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded shrink-0" style={{ background: '#FEF2F2', color: '#B91C1C' }}>&minus;{q.negative_marks} if wrong</span>
                  )}
                </div>

                {q.type === 'numerical' ? (
                  <input
                    type="text" inputMode="decimal"
                    value={answers[q.question_number] || ''}
                    onChange={e => handleAnswerChange(q.question_number, e.target.value)}
                    placeholder="Enter your numeric answer"
                    data-testid={`question-${i}-numeric-input`}
                    className="w-full max-w-xs rounded-xl px-3 py-2 text-[13px] outline-none"
                    style={{ background: 'var(--st-bg)', border: '2px solid var(--st-border)', color: 'var(--st-ink)' }}
                  />
                ) : (
                  <div className="space-y-2" data-testid={`question-${i}-options`}>
                    {(q.options || []).map((opt, j) => {
                      const picked = answers[q.question_number] === opt;
                      return (
                        <label key={j} className="quiz-option flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 border-2 cursor-pointer transition-all"
                          style={{ borderColor: picked ? 'var(--st-primary)' : 'var(--st-border)', background: picked ? 'color-mix(in srgb, var(--st-primary) 8%, transparent)' : 'transparent' }}>
                          <input type="radio" name={`q-${q.question_number}`} className="sr-only"
                            checked={picked} onChange={() => handleAnswerChange(q.question_number, opt)} />
                          <span className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0" style={{ borderColor: picked ? 'var(--st-primary)' : 'var(--st-border)' }}>
                            {picked && <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--st-primary)' }} />}
                          </span>
                          <span className="flex-1 text-[13px]" style={{ color: 'var(--st-ink)' }}><MathText text={opt} /></span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="submit-quiz-button"
          className="rounded-2xl px-5 py-3 text-[14px] font-extrabold text-white inline-flex items-center gap-2 disabled:opacity-50"
          style={{ background: 'var(--st-primary)' }}>
          {submitting ? 'Submitting...' : 'Submit Quiz'} <Award className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// StartTestPopup — the gate a timed test (quiz.time_limit set) shows before
// any question is visible. The countdown only starts once the student taps
// Start, so they aren't burning exam time reading the rules.
// ═══════════════════════════════════════════════════════════════════════════
function StartTestPopup({ quiz, gradableCount, examPrep, onStart, onBack }) {
  const hasNegativeMarking = (quiz.questions || []).some(q => (q.negative_marks || 0) > 0);
  return (
    <div className="max-w-lg mx-auto px-4 py-16" data-testid="start-test-popup">
      <div className="rounded-3xl border p-8 text-center space-y-5" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
        <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center" style={{ background: examPrep.goldTint, color: examPrep.gold }}>
          <Clock className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-xl font-extrabold" style={{ color: 'var(--st-ink)', fontFamily: 'var(--st-font-heading)' }}>{quiz.title}</h1>
          <p className="text-[13px] mt-2" style={{ color: 'var(--st-mute)' }}>
            {gradableCount} question{gradableCount === 1 ? '' : 's'} &middot; {quiz.time_limit} minutes
            {hasNegativeMarking && <> &middot; negative marking</>}
          </p>
        </div>
        <div className="rounded-xl p-3.5 text-[12px] text-left" style={{ background: 'var(--st-bg)', color: 'var(--st-mute)' }}>
          Once you start, the timer runs continuously and the test submits itself automatically when time runs out — even if you haven&rsquo;t answered everything.
        </div>
        <div className="flex gap-2.5">
          <button onClick={onBack} className="flex-1 rounded-xl px-4 py-3 text-[13px] font-bold border" style={{ borderColor: 'var(--st-border)', color: 'var(--st-ink)' }}>
            Back
          </button>
          <button onClick={onStart} data-testid="start-test-button"
            className="flex-1 rounded-xl px-4 py-3 text-[13.5px] font-extrabold text-white inline-flex items-center justify-center gap-2"
            style={{ background: examPrep.gold }}>
            <Play className="w-4 h-4" /> Start test
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Result — score, per-question review, and (exam-mode only) the time-report
// palette grid: a colored/height-encoded strip echoing the cell grid every
// JEE/NEET student recognizes from real exam software. Shown both for a
// just-submitted attempt and, unless retaking, for a previously-attempted
// test's most recent result -- onRetake is the only way back into a fresh
// attempt.
// ═══════════════════════════════════════════════════════════════════════════
function ResultView({ quiz, result, timeReport, answers, onDiscuss, onRetake, onBack }) {
  const { theme } = useStudent();
  const examPrep = getExamPrepColors(theme?.id);
  const gradableQuestions = (quiz.questions || []).filter(q => GRADABLE_TYPES.has(q.type));
  const pct = Math.round(result.percentage || 0);
  const tier = pct >= 70 ? { bg: '#F0FDF4', fg: '#16A34A', label: 'Excellent!' }
    : pct >= 50 ? { bg: '#FFFBEB', fg: '#D97706', label: 'Good job!' }
    : { bg: '#FEF2F2', fg: '#DC2626', label: 'Keep practicing!' };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-5" data-testid="quiz-result">
      <div className="flex items-center justify-between gap-3">
        <button onClick={onBack} className="text-[12px] font-semibold inline-flex items-center gap-1 opacity-70 hover:opacity-100">
          <ArrowLeft className="w-4 h-4" /> Back to My Quizzes
        </button>
        <button onClick={onRetake} data-testid="retake-test-button"
          className="text-[12px] font-extrabold rounded-full px-3.5 py-1.5 inline-flex items-center gap-1.5 text-white"
          style={{ background: examPrep.gold }}>
          <RotateCcw className="w-3.5 h-3.5" /> Retake
        </button>
      </div>

      <div className="rounded-2xl border p-8 text-center" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
        <div className="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: tier.bg }}>
          <Award className="w-10 h-10" style={{ color: tier.fg }} />
        </div>
        <h2 className="text-3xl font-extrabold mb-1" data-testid="result-percentage" style={{ color: 'var(--st-ink)', fontFamily: 'var(--st-font-heading)' }}>{pct}%</h2>
        <p className="text-[13px] mb-3" style={{ color: 'var(--st-mute)' }}>You scored {result.score}/{result.max_score} marks</p>
        <span className="inline-block rounded-full px-3 py-1 text-[11px] font-extrabold" style={{ background: tier.bg, color: tier.fg }}>{tier.label}</span>
      </div>

      {timeReport && <TimeReportGrid report={timeReport} questions={gradableQuestions} />}

      <div className="rounded-2xl border p-5" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
        <div className="text-[13px] font-bold mb-4" style={{ fontFamily: 'var(--st-font-heading)' }}>Answer Review</div>
        <div className="space-y-5">
          {gradableQuestions.map((q, i) => {
            const submitted = (result.answers || []).find(a => a.question_number === q.question_number);
            const userAnswer = submitted?.answer ?? answers[q.question_number];
            const isCorrect = answersMatch(q.type, userAnswer, q.correct_answer);
            return (
              <div key={q.question_number} className="space-y-2.5" data-testid={`review-question-${i}`}>
                <div className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: isCorrect ? '#F0FDF4' : '#FEF2F2', color: isCorrect ? '#16A34A' : '#DC2626' }}>
                    {isCorrect ? <Check className="w-4 h-4" /> : <XIcon className="w-4 h-4" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13.5px] mb-2" style={{ color: 'var(--st-ink)' }}><MathText text={q.question} /></div>

                    {q.type === 'numerical' ? (
                      <div className="text-[12.5px] space-y-1">
                        <div><span style={{ color: 'var(--st-mute)' }}>Your answer:</span> <b style={{ color: 'var(--st-ink)' }}>{userAnswer ?? '—'}</b></div>
                        {!isCorrect && <div><span style={{ color: 'var(--st-mute)' }}>Correct:</span> <b style={{ color: '#16A34A' }}>{q.correct_answer}</b></div>}
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {(q.options || []).map((opt, j) => {
                          const isUserAnswer = opt === userAnswer;
                          const isCorrectAnswer = opt === q.correct_answer;
                          return (
                            <div key={j} className="px-3 py-2 rounded-lg border text-[12.5px] flex items-center gap-2"
                              style={{
                                borderColor: isCorrectAnswer ? '#16A34A' : isUserAnswer ? '#DC2626' : 'var(--st-border)',
                                background: isCorrectAnswer ? '#F0FDF4' : isUserAnswer ? '#FEF2F2' : 'transparent',
                              }}>
                              <span style={{ color: 'var(--st-ink)' }}><MathText text={opt} /></span>
                              {isCorrectAnswer && <span className="ml-auto text-[10px] font-extrabold" style={{ color: '#16A34A' }}>Correct</span>}
                              {isUserAnswer && !isCorrectAnswer && <span className="ml-auto text-[10px] font-extrabold" style={{ color: '#DC2626' }}>Your answer</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {q.explanation && (
                      <div className="mt-2.5 p-3 rounded-lg text-[12px]" style={{ background: 'var(--st-bg)', color: 'var(--st-mute)' }}>
                        <strong style={{ color: 'var(--st-ink)' }}>Explanation:</strong> <MathText text={q.explanation} />
                      </div>
                    )}

                    {!isCorrect && quiz.published_lesson_id && (
                      <button
                        onClick={() => onDiscuss(q, userAnswer)}
                        data-testid={`discuss-wrong-answer-${i}`}
                        className="mt-2.5 text-[11.5px] font-bold inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                        style={{ background: examPrep.gold, color: '#fff' }}>
                        <MessageCircle className="w-3.5 h-3.5" /> Discuss this with your tutor
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TimeReportGrid({ report, questions }) {
  const { theme } = useStudent();
  const examPrep = getExamPrepColors(theme?.id);
  const rows = report.questions || [];
  const maxTime = Math.max(1, ...rows.map(r => r.time_spent_seconds || 0));
  return (
    <div className="rounded-2xl border p-5" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid="time-report-grid">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[13px] font-bold" style={{ fontFamily: 'var(--st-font-heading)' }}>How you spent your time</div>
        <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: examPrep.gold, color: '#fff' }}>Exam Prep</span>
      </div>
      <p className="text-[11.5px] mb-4" style={{ color: 'var(--st-mute)' }}>
        {report.total_time_seconds}s total &middot; {report.average_time_seconds}s avg per answered question &middot; {report.skipped_count} skipped
      </p>
      <div className="flex items-end gap-1.5 h-24">
        {rows.map((r, i) => {
          const heightPct = Math.max(8, Math.round((r.time_spent_seconds / maxTime) * 100));
          const color = r.skipped ? '#CBD5E1' : r.is_correct ? '#16A34A' : '#DC2626';
          return (
            <div key={r.question_number ?? i} className="flex-1 h-full flex flex-col items-center justify-end gap-1" title={`Q${i + 1} · ${r.time_spent_seconds}s`}>
              <div className="w-full rounded-t-md transition-all" style={{ height: `${heightPct}%`, background: color, minWidth: 6 }} data-testid={`time-report-cell-${i}`} />
              <span className="text-[9px] font-bold opacity-50">{i + 1}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[10.5px]" style={{ color: 'var(--st-mute)' }}>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#16A34A' }} /> Correct</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#DC2626' }} /> Incorrect</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#CBD5E1' }} /> Skipped</span>
      </div>
    </div>
  );
}

export default StudentQuizAttempt;
